import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchManagerSeasonRaces, fetchManagerSeriesSeasons } from "../../../lib/managerApi";
import {
    getJob,
    runRacePointsDb,
    runResultsImportDb,
    streamJobLogs,
    uploadIracingJson,
} from "../../../lib/ligamanagerApi";

function prettyErr(error) {
    if (!error) return "Unbekannter Fehler";
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message || "Fehler";
    if (typeof error === "object") {
        if (error.detail) return typeof error.detail === "string" ? error.detail : JSON.stringify(error.detail);
        if (error.message) return error.message;
    }
    return String(error);
}

function ProgressBar({ progress }) {
    const total = Number(progress?.total || 0);
    const step = Number(progress?.step || 0);
    const percent = total > 0 ? Math.round((Math.max(0, step) / Math.max(1, total)) * 100) : 0;

    if (!progress?.status && !total) return null;

    return (
        <div style={{ width: "100%", marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", opacity: 0.85, fontFamily: "var(--ff-Everett)", fontSize: 12 }}>
                <div>{progress?.label || progress?.status || "Status"}</div>
                <div>{total > 0 ? `${step}/${total} - ${percent}%` : progress?.status}</div>
            </div>
            <div
                style={{
                    marginTop: 6,
                    height: 10,
                    borderRadius: 999,
                    border: "1px solid rgba(232,253,83,.35)",
                    background: "rgba(232,253,83,.08)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${percent}%`,
                        background: "rgba(232,253,83,.75)",
                    }}
                />
            </div>
        </div>
    );
}

export default function ResultsImport() {
    const [seriesRows, setSeriesRows] = useState([]);
    const [seriesKey, setSeriesKey] = useState("gt3x");
    const [seasonId, setSeasonId] = useState("");
    const [races, setRaces] = useState([]);
    const [raceId, setRaceId] = useState("");

    const [file, setFile] = useState(null);
    const [uploadId, setUploadId] = useState("");
    const [uploadName, setUploadName] = useState("");
    const [jobId, setJobId] = useState(null);
    const [summary, setSummary] = useState(null);

    const [logItems, setLogItems] = useState([]);
    const [progress, setProgress] = useState({ status: "", step: 0, total: 0, label: "" });
    const [error, setError] = useState("");
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [uploadBusy, setUploadBusy] = useState(false);
    const [importBusy, setImportBusy] = useState(false);
    const [pointsBusy, setPointsBusy] = useState(false);

    const stopStreamRef = useRef(null);
    const pollTimerRef = useRef(null);
    const logsRef = useRef(null);

    const selectedSeries = useMemo(
        () => seriesRows.find((row) => row.series_key === seriesKey) || null,
        [seriesRows, seriesKey],
    );
    const seasons = useMemo(() => selectedSeries?.seasons || [], [selectedSeries]);
    const selectedSeason = useMemo(
        () => seasons.find((season) => String(season.id) === String(seasonId)) || null,
        [seasons, seasonId],
    );
    const selectedRace = useMemo(
        () => races.find((race) => String(race.id) === String(raceId)) || null,
        [races, raceId],
    );

    const busy = loadingMeta || uploadBusy || importBusy || pointsBusy;
    const fileLabel = file ? file.name : "JSON auswaehlen...";
    const sectionStyle = {
        border: "1px solid var(--border-soft)",
        borderRadius: 18,
        padding: 16,
        background: "rgba(232,253,83,.045)",
    };

    function pushUi(msg) {
        setLogItems((prev) => [...prev, { level: "INFO", msg: String(msg || ""), uiOnly: true }]);
    }

    function pushLog(item) {
        setLogItems((prev) => [
            ...prev,
            {
                seq: typeof item?.seq === "number" ? item.seq : null,
                level: String(item?.level || "INFO").toUpperCase(),
                ts: item?.ts || null,
                msg: item?.msg ?? "",
                uiOnly: false,
            },
        ]);
    }

    function stopStream() {
        try {
            if (stopStreamRef.current) stopStreamRef.current();
        } catch {}
        stopStreamRef.current = null;
    }

    function stopPolling() {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }

    async function pollJob(id) {
        const job = await getJob(id);
        setProgress({
            status: job?.status || "",
            step: Number(job?.progress_step || 0),
            total: Number(job?.progress_total || 0),
            label: job?.progress_label || "",
        });
        if (job?.summary_json) setSummary(job.summary_json);
        if (["done", "error", "canceled"].includes(String(job?.status || ""))) {
            stopPolling();
            setImportBusy(false);
            setPointsBusy(false);
        }
    }

    function startPolling(id) {
        stopPolling();
        pollJob(id).catch(() => {});
        pollTimerRef.current = setInterval(() => {
            pollJob(id).catch(() => {});
        }, 800);
    }

    useEffect(() => {
        let active = true;

        async function load() {
            setLoadingMeta(true);
            setError("");
            try {
                const data = await fetchManagerSeriesSeasons();
                if (!active) return;

                const rows = Array.isArray(data?.series) ? data.series : [];
                setSeriesRows(rows);

                const preferred = rows.find((row) => row.series_key === "gt3x") || rows[0] || null;
                if (preferred) {
                    setSeriesKey(preferred.series_key);
                    const activeSeason = (preferred.seasons || []).find((season) => season.status === "active");
                    const nextSeason = activeSeason || (preferred.seasons || [])[0] || null;
                    setSeasonId(nextSeason ? String(nextSeason.id) : "");
                }
            } catch (err) {
                if (active) setError(prettyErr(err));
            } finally {
                if (active) setLoadingMeta(false);
            }
        }

        load();
        return () => {
            active = false;
            stopStream();
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!seasonId) {
            setRaces([]);
            setRaceId("");
            return;
        }

        let active = true;

        async function loadRaces() {
            setLoadingMeta(true);
            setError("");
            try {
                const data = await fetchManagerSeasonRaces(seasonId);
                if (!active) return;

                const nextRaces = Array.isArray(data?.races) ? data.races : [];
                setRaces(nextRaces);
                setRaceId(nextRaces[0] ? String(nextRaces[0].id) : "");
            } catch (err) {
                if (active) setError(prettyErr(err));
            } finally {
                if (active) setLoadingMeta(false);
            }
        }

        loadRaces();
        return () => {
            active = false;
        };
    }, [seasonId]);

    useEffect(() => {
        const el = logsRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [logItems]);

    function onSeriesChange(nextSeriesKey) {
        setSeriesKey(nextSeriesKey);
        const nextSeries = seriesRows.find((row) => row.series_key === nextSeriesKey);
        const activeSeason = (nextSeries?.seasons || []).find((season) => season.status === "active");
        const nextSeason = activeSeason || (nextSeries?.seasons || [])[0] || null;
        setSeasonId(nextSeason ? String(nextSeason.id) : "");
        setRaceId("");
        setRaces([]);
    }

    function onFileChange(nextFile) {
        setFile(nextFile);
        setUploadId("");
        setUploadName("");
    }

    async function uploadSelectedFile() {
        if (uploadId) return uploadId;

        if (!file) {
            throw new Error("Bitte zuerst eine JSON Datei auswaehlen.");
        }

        setUploadBusy(true);
        pushUi("JSON wird an das Backend uebergeben...");
        try {
            const result = await uploadIracingJson(file);
            if (!result?.upload_id) throw new Error("Upload fehlgeschlagen: upload_id fehlt.");
            setUploadId(result.upload_id);
            setUploadName(result.filename || file.name);
            pushUi(`Datei bereit: ${result.upload_id}`);
            return result.upload_id;
        } finally {
            setUploadBusy(false);
        }
    }

    async function onRunImport() {
        setError("");
        setSummary(null);
        setJobId(null);
        setProgress({ status: "", step: 0, total: 0, label: "" });
        setLogItems([]);
        stopStream();
        stopPolling();

        if (!selectedSeason) return setError("Bitte Saison auswaehlen.");
        if (!selectedRace) return setError("Bitte Rennen auswaehlen.");
        if (!uploadId) return setError("Bitte zuerst JSON hochladen.");

        setImportBusy(true);
        try {
            const nextUploadId = await uploadSelectedFile();
            pushUi("DB Result Import Job wird gestartet...");
            const result = await runResultsImportDb({
                upload_id: nextUploadId,
                series_key: seriesKey,
                season_id: selectedSeason.id,
                season_key: selectedSeason.season_key,
                race_id: selectedRace.id,
                race_tag: selectedRace.race_tag,
            });

            if (!result?.job_id) throw new Error("Backend hat keine job_id geliefert.");

            setJobId(result.job_id);
            pushUi(`Job gestartet: #${result.job_id}`);
            startPolling(result.job_id);

            stopStreamRef.current = streamJobLogs({
                job_id: result.job_id,
                onLog: pushLog,
                onDone: async (status) => {
                    pushUi(`Job beendet: ${status}`);
                    try {
                        await pollJob(result.job_id);
                    } catch {}
                    if (String(status).trim() === "error") {
                        setError("Job beendet mit Fehler. Siehe Logs.");
                    }
                    setImportBusy(false);
                    setPointsBusy(false);
                    stopStream();
                    stopPolling();
                },
                onError: (err) => {
                    const msg = prettyErr(err);
                    pushUi(`SSE FEHLER: ${msg}`);
                    setError(msg);
                    setImportBusy(false);
                    setPointsBusy(false);
                    stopStream();
                    stopPolling();
                },
            });
        } catch (err) {
            const msg = prettyErr(err);
            setError(msg);
            pushUi(`FEHLER: ${msg}`);
            setImportBusy(false);
            stopStream();
            stopPolling();
        }
    }

    async function onRunPoints() {
        setError("");
        setSummary(null);
        setJobId(null);
        setProgress({ status: "", step: 0, total: 0, label: "" });
        setLogItems([]);
        stopStream();
        stopPolling();

        if (!selectedSeason) return setError("Bitte Saison auswaehlen.");
        if (!selectedRace) return setError("Bitte Rennen auswaehlen.");

        setPointsBusy(true);
        try {
            pushUi("DB Punkte Job wird gestartet...");
            const result = await runRacePointsDb({
                series_key: seriesKey,
                season_id: selectedSeason.id,
                season_key: selectedSeason.season_key,
                race_id: selectedRace.id,
                race_tag: selectedRace.race_tag,
                result_import_id: summary?.result_import_id || null,
            });

            if (!result?.job_id) throw new Error("Backend hat keine job_id geliefert.");

            setJobId(result.job_id);
            pushUi(`Job gestartet: #${result.job_id}`);
            startPolling(result.job_id);

            stopStreamRef.current = streamJobLogs({
                job_id: result.job_id,
                onLog: pushLog,
                onDone: async (status) => {
                    pushUi(`Job beendet: ${status}`);
                    try {
                        await pollJob(result.job_id);
                    } catch {}
                    if (String(status).trim() === "error") {
                        setError("Job beendet mit Fehler. Siehe Logs.");
                    }
                    setPointsBusy(false);
                    stopStream();
                    stopPolling();
                },
                onError: (err) => {
                    const msg = prettyErr(err);
                    pushUi(`SSE FEHLER: ${msg}`);
                    setError(msg);
                    setPointsBusy(false);
                    stopStream();
                    stopPolling();
                },
            });
        } catch (err) {
            const msg = prettyErr(err);
            setError(msg);
            pushUi(`FEHLER: ${msg}`);
            setPointsBusy(false);
            stopStream();
            stopPolling();
        }
    }

    const sessionSummary = summary?.sessions || {};

    return (
        <div className="rv-page manager-modern-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Orga</div>
                        <h1 className="rv-page-title">Results importieren</h1>
                        <p className="rv-page-subtitle">
                            DB-only Bereich fuer Result Import und Punkteberechnung. Der bestehende Google-Sheets Standings Import bleibt unberuehrt.
                        </p>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}

            <div className="card rv-filter-card" style={{ display: "grid", gap: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontWeight: 800, fontSize: 13 }}>Serie</span>
                        <select className="input" value={seriesKey} onChange={(event) => onSeriesChange(event.target.value)} disabled={busy}>
                            {seriesRows.map((series) => (
                                <option key={series.id} value={series.series_key}>
                                    {series.display_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontWeight: 800, fontSize: 13 }}>Saison</span>
                        <select className="input" value={seasonId} onChange={(event) => setSeasonId(event.target.value)} disabled={busy || !seasons.length}>
                            {seasons.map((season) => (
                                <option key={season.id} value={season.id}>
                                    {season.display_name} ({season.status})
                                </option>
                            ))}
                        </select>
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontWeight: 800, fontSize: 13 }}>Rennen</span>
                        <select className="input" value={raceId} onChange={(event) => setRaceId(event.target.value)} disabled={busy || !races.length}>
                            {races.map((race) => (
                                <option key={race.id} value={race.id}>
                                    {race.race_tag} - {race.track_name}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>1. Result Import DB</h2>
                    <p className="muted" style={{ margin: 0 }}>
                        Schreibt den aktiven iRacing Import und alle Session Results in die Datenbank.
                    </p>

                    <div style={sectionStyle}>
                        <div style={{ display: "grid", gap: 8 }}>
                            <strong>iRacing JSON Datei</strong>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <input
                                    id="rv-results-json-upload"
                                    type="file"
                                    accept=".json,application/json"
                                    onChange={(event) => onFileChange(event.target.files?.[0] || null)}
                                    style={{ display: "none" }}
                                    disabled={busy}
                                />
                                <label htmlFor="rv-results-json-upload" className="btn" style={{ cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
                                    {file ? "Datei aendern" : "Datei auswaehlen"}
                                </label>
                                <div
                                    style={{
                                        padding: "9px 12px",
                                        borderRadius: 12,
                                        border: "1px solid rgba(255,255,255,.14)",
                                        background: "rgba(255,255,255,.03)",
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={fileLabel}
                                >
                                    {fileLabel}
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button className="btn btn--accent" onClick={onRunImport} disabled={busy || !file || !selectedSeason || !selectedRace}>
                                    {uploadBusy ? "Datei..." : importBusy ? "Import..." : "Result Import DB starten"}
                                </button>
                            </div>
                            {uploadId && (
                                <div className="muted">
                                    Datei bereit: <b>{uploadName}</b> - ID: <code>{uploadId}</code>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>2. Punkte fuer DB berechnen</h2>
                    <p className="muted" style={{ margin: 0 }}>
                        Berechnet Driver-, Team- und Markenpunkte aus dem aktiven DB Result Import des gewaehlten Rennens.
                    </p>
                    <div style={sectionStyle}>
                        <button className="btn btn--accent" onClick={onRunPoints} disabled={busy || !selectedSeason || !selectedRace}>
                            {pointsBusy ? "Punkte..." : "Punkte fuer DB berechnen"}
                        </button>
                    </div>
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <h2 style={{ margin: 0 }}>Job & Status</h2>
                        {jobId && <span className="pill pill--gesamt">Job #{jobId}</span>}
                    </div>
                    <ProgressBar progress={progress} />

                    {summary?.ok && (
                        <div style={sectionStyle}>
                            <b>{summary.job_type === "race_points_db" ? "Punkte gespeichert" : "Import gespeichert"}</b>
                            <div style={{ marginTop: 8, display: "flex", gap: 14, flexWrap: "wrap", opacity: 0.92 }}>
                                <span>result_import_id: {summary.result_import_id}</span>
                                <span>season_id: {summary.season_id}</span>
                                <span>race_id: {summary.race_id}</span>
                                {summary.import_version !== undefined && <span>version: {summary.import_version}</span>}
                                {summary.rows !== undefined && <span>rows: {summary.rows}</span>}
                                {summary.driver_point_rows !== undefined && <span>driver points: {summary.driver_point_rows}</span>}
                                {summary.team_point_rows !== undefined && <span>team points: {summary.team_point_rows}</span>}
                                {summary.brand_point_rows !== undefined && <span>brand points: {summary.brand_point_rows}</span>}
                                {summary.dns_count !== undefined && <span>DNS: {summary.dns_count}</span>}
                                {summary.dnf_count !== undefined && <span>DNF: {summary.dnf_count}</span>}
                                {summary.dsq_count !== undefined && <span>DSQ: {summary.dsq_count}</span>}
                                {summary.season_cars_set !== undefined && <span>Saisonautos gesetzt: {summary.season_cars_set}</span>}
                            </div>
                            {!!Object.keys(sessionSummary).length && (
                                <div style={{ marginTop: 8, opacity: 0.9 }}>
                                    Sessions: {Object.entries(sessionSummary).map(([key, value]) => `${key}: ${value}`).join(", ")}
                                </div>
                            )}
                        </div>
                    )}
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <h2 style={{ margin: 0 }}>Logs</h2>
                        <button className="btn" onClick={() => setLogItems([])} disabled={!logItems.length}>
                            Clear Logs
                        </button>
                    </div>
                    <div
                        ref={logsRef}
                        style={{
                            height: 360,
                            overflow: "auto",
                            border: "1px solid rgba(255,255,255,.14)",
                            borderRadius: 14,
                            padding: 12,
                            background: "rgba(0,0,0,.24)",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                            fontSize: 12,
                            lineHeight: 1.45,
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {logItems.length ? (
                            logItems.map((item, index) => (
                                <div
                                    key={`${item.seq ?? "ui"}-${index}`}
                                    style={{
                                        color: item.level === "ERROR" ? "#ff8f8f" : item.level === "WARN" ? "#ffd27a" : "rgba(255,255,255,.86)",
                                        opacity: item.uiOnly ? 0.78 : 1,
                                    }}
                                >
                                    {item.seq ? `${item.seq}. ` : ""}
                                    {item.msg}
                                </div>
                            ))
                        ) : (
                            <div style={{ opacity: 0.55 }}>Noch keine Logs.</div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
